import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../images/logo.png';
import slogan from '../images/slogan.png';
import { 
  FileText, 
  Settings, 
  LogOut, 
  Menu, 
  X,
  User
} from 'lucide-react';

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    return location.pathname === path;
  };

  const navItems = [];

  // Sadece öğrenciler için başvuru menüsü
  const studentItems = [
    { path: '/applications', label: 'Başvurularım', icon: FileText },
  ];

  const adminItems = [
    { path: '/admin', label: 'Yönetim Paneli', icon: Settings },
  ];


  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="container-fluid mx-auto px-4">
        <div className="flex justify-between items-center h-16 relative">
          {/* Logo ve Diğer Menüler - Sol */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Link to="/dashboard" className="flex items-center space-x-2 mr-4">
              <img 
                src={logo} 
                alt="Korkmazlar Yurt Logo" 
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-gray-900 hidden lg:inline">Korkmazlar Yurt</span>
            </Link>
          </div>

          {/* Slogan - Orta */}
          <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center px-20">
            <img 
              src={slogan} 
              alt="Slogan" 
              className="h-16 w-auto max-w-5xl"
            />
          </div>

          {/* Sağ Menü - Başvurular, Admin, Kullanıcı */}
          <div className="hidden md:flex items-center space-x-1 flex-shrink-0">
            {/* Sadece öğrenciler için başvuru menüsü */}
            {user?.role === 'student' && studentItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
            
            {/* Sadece admin'ler için yönetim menüsü */}
            {user?.role === 'admin' && adminItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-red-100 text-red-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              );
            })}

            {/* User Menu */}
            <div className="flex items-center space-x-4 ml-2">
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <User className="w-4 h-4" />
                <span className="hidden lg:inline">{user?.username}</span>
                {user?.role === 'admin' && (
                  <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                    Admin
                  </span>
                )}
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden lg:inline">Çıkış</span>
              </button>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex items-center justify-center w-8 h-8 text-gray-600 hover:text-gray-900"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="md:hidden py-4 border-t">
            <div className="space-y-2">
              {/* Sadece öğrenciler için başvuru menüsü */}
              {user?.role === 'student' && studentItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary-100 text-primary-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              
              {/* Sadece admin'ler için yönetim menüsü */}
              {user?.role === 'admin' && adminItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsOpen(false)}
                    className={`flex items-center space-x-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive(item.path)
                        ? 'bg-red-100 text-red-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              
              <div className="pt-4 border-t">
                <div className="flex items-center space-x-2 px-3 py-2 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  <span>{user?.username}</span>
                  {user?.role === 'admin' && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">
                      Admin
                    </span>
                  )}
                </div>
                <button
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}
                  className="flex items-center space-x-3 px-3 py-2 w-full text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  <span>Çıkış</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation; 